import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme, Box, Paper, Typography, Button, Tooltip, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  DataGridPro,
  GridColDef,
  gridDateComparator,
  useGridApiContext,
  gridPageSelector,
  gridPageSizeSelector,
  useGridApiRef,
  GridToolbar,
  GridTreeDataGroupingCell,
} from '@mui/x-data-grid-pro';
import { XGridToolbar, IXGridBulkActionButton } from '../../Common/XGrid/Toolbar/XGridToolbar';
import { selectSearchResults } from 'store/redux-store/global-search/selectors';
import { selectTaskSearchData, selectTaskSearchGridRefreshNonce } from '../../Tasks/store/Tasks.selectors';
import { taskDetailsActions } from '../../Tasks/store/Tasks.redux';
import { DrawerFitted } from '../../Common/Drawer/Drawer';
import { SearchDrawerContent, TASKS_DEFAULT_PAGE_SIZE } from '../SearchResultsPage';
import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';
import { globalSearchActions } from 'store/redux-store/global-search/slice';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { IParentTaskSearchResponse, ISubTaskSearchResponse } from 'app/models/Tasks/Tasks';
import { selectGridState } from '../../Common/Grid/GridStateManagement/GridState.selectors';
import { shouldClearForeignFiltersOnGridEntry } from '../../Common/Grid/GridStateManagement/gridFilterOwnership';
import { gridStateActions } from '../../Common/Grid/GridStateManagement/GridState.redux';
import { Pagination } from 'app/ComponentLibrary/Pagination/Pagination';
import { useGridSelector } from '@mui/x-data-grid-pro';
import { convertToPST } from 'app/helpers/dateHelper';
import { StatusBadgeGrid } from '../../Tasks/CustomComponents/StatusBadgeGrid';
import { FormProvider, useForm } from 'react-hook-form';
import { TaskSearchFilters } from './TaskSearchFilters';
import { SearchTasksChips } from './SearchTasksChips';
import { TaskSearchFilterName } from '../../Tasks/TaskManagement/TaskFilters/TaskFilterUtilities';
import { defaultFilterValues as taskDefaultFilterValues, taskSearchFilterName, TASKS_DEFAULT_COLUMN_VISIBILITY } from './SearchUtils';
import { initialUnitAggregateSearch } from 'app/ComponentLibrary/Filter/UnitSearch/Index';
import { initialCandidateAggregateSearch } from 'app/ComponentLibrary/Filter/CandidateSearch';
import { initialFacilityAggregateSearch } from 'app/ComponentLibrary/Filter/FacilitySearch/Index';
import { selectUser } from 'oidc/user.selectors';
import { reconcileSelfEmployeeNameEntries } from 'oidc/selfEmployeeNameReconciler';
import { userDevicePreferenceActions } from 'oidc/UserDevicePreference/userPreference.redux';
import { selectUserPreferenceLoaded } from 'oidc/UserDevicePreference/userPreference.selectors';
import { RootState } from 'types';
import { missingField } from 'app/constants';
import { ViewEditTaskModal } from './ViewEditTaskModal/ViewEditTaskModal';
import { revalidateChipSelection } from '../helper';
import { getSingleBooleanFilterValue, matchesBooleanFilter } from '@AMIEWEB/GlobalSearch/Tasks/taskSearchBooleanFilterUtils';
import { SearchFilterAction } from '../FilterSpecComponents/SearchFilterAction';
import { SearchType } from 'app/models/GlobalSearch/GlobalSearch';
import {
  getSessionValue,
  setSessionValue,
  removeSessionValue,
  SessionKey,
  StorageType,
} from 'utils/customHooks/sessionStorage/sessionHelpers';
import _ from 'lodash';
import { TaskSearchCandidateBrandCell } from './taskSearchCandidateBrandCell';
import {
  fromGridSortModel,
  isServerSortableField,
  toGridSortModel,
} from './taskSearchSortHelpers';

// Helper function to format date values with time
const formatDate = (value: string | null | undefined): string => {
  if (!value) return missingField;
  const formatted = convertToPST(value, 'MM/DD/YYYY hh:mm A');
  // Check for invalid date strings
  if (formatted === 'Invalid Date' || formatted.includes('Invalid')) {
    return missingField;
  }
  return formatted;
};

// Helper function to format date values without time
const formatDateOnly = (value: string | null | undefined): string => {
  if (!value) return missingField;
  const formatted = convertToPST(value, 'MM/DD/YYYY');
  // Check for invalid date strings
  if (formatted === 'Invalid Date' || formatted.includes('Invalid')) {
    return missingField;
  }
  return formatted;
};

const toDateOrNull = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

// Show double dash for null, undefined, or 0
const idOrDash = (val: number | null | undefined): string =>
  val != null && val !== 0 ? String(val) : missingField;

// Sort ID columns as integers (e.g. 10000 > 9999) instead of lexicographically
const numericIdSortComparator = (v1, v2) => {
  const n1 = typeof v1 === 'number' && !Number.isNaN(v1) ? v1 : Number(v1);
  const n2 = typeof v2 === 'number' && !Number.isNaN(v2) ? v2 : Number(v2);
  const num1 = Number.isNaN(n1) ? Number.NEGATIVE_INFINITY : n1;
  const num2 = Number.isNaN(n2) ? Number.NEGATIVE_INFINITY : n2;
  return num1 - num2;
};

const whiteTooltipProps = {
  componentsProps: {
    tooltip: {
      sx: {
        backgroundColor: '#fff',
        color: '#333333',
        border: '1px solid rgba(224, 224, 224, 1)',
        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
        fontSize: '12px',
      },
    },
    arrow: { sx: { color: '#fff' } },
  },
};

const OverflowTooltipWrapper = ({
  tooltipText,
  children,
  sx: boxSx,
  onClick,
}: {
  tooltipText: string;
  children: React.ReactNode;
  sx?: Record<string, unknown>;
  onClick?: (e: React.MouseEvent) => void;
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = React.useState(false);

  const checkOverflow = React.useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const nestedSpan = el.querySelector<HTMLElement>('.MuiDataGrid-treeDataGroupingCell > span');
    if (nestedSpan) {
      setIsOverflowing(nestedSpan.scrollWidth > nestedSpan.clientWidth);
      return;
    }
    setIsOverflowing(el.scrollWidth > el.clientWidth);
  }, []);

  React.useEffect(() => {
    checkOverflow();
  }, [tooltipText, checkOverflow]);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkOverflow]);

  return (
    <Tooltip title={tooltipText} arrow disableHoverListener={!isOverflowing} {...whiteTooltipProps}>
      <Box
        ref={ref}
        sx={{ width: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ...boxSx }}
        onClick={onClick}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

const OverflowTooltipCell = ({ value }: { value: string }) => (
  <OverflowTooltipWrapper tooltipText={value || ''}>
    {value}
  </OverflowTooltipWrapper>
);

const useStyles = makeStyles()((theme) => ({
  root: {
    minHeight: 'calc(100vh - 105px)', 
    padding: '12px',
  },
  mainPanelRoot: {
    width: '100%',
  },
  container: {
    flexWrap: 'nowrap',
  },
}));

// Custom pagination component following XGrid pattern
interface TasksGridPaginationProps {
  loading?: boolean;
}

const TasksGridPagination: React.FC<TasksGridPaginationProps> = ({ loading }) => {
  const apiRef = useGridApiContext();
  const page = useGridSelector(apiRef, gridPageSelector);
  const pageSize = useGridSelector(apiRef, gridPageSizeSelector);
  const taskSearchData = useSelector(selectTaskSearchData);
  const dispatch = useDispatch();

  const rowCount = taskSearchData?.paging?.total || 0;

  return (
    <Pagination
      isLoading={loading ?? false}
      handleResultsPerPageChange={(value, _page) => {
        dispatch(gridStateActions.setPageSize(value));
        dispatch(gridStateActions.setPageNumber(1));
        apiRef?.current?.setPageSize(value);
        apiRef?.current?.setPage(0);
      }}
      handlePageChange={(value, _) => {
        dispatch(gridStateActions.setPageNumber(value));
        apiRef?.current?.setPage(value - 1);
      }}
      pageOptions={[50, 100, 150, 200, 250]}
      pagination={{
        page: (page || 0) + 1,
        resultsPerPage: pageSize || TASKS_DEFAULT_PAGE_SIZE,
        totalResults: rowCount,
      }}
    />
  );
};

interface TasksSearchWrapperProps {
  gridTag: string;
  loading: boolean;
  getSearchResults: (params: unknown) => void;
  searchString?: string;
}

export const TasksSearchWrapper: React.FC<TasksSearchWrapperProps> = ({
  gridTag,
  loading,
  getSearchResults,
  searchString,
}) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { drawerData } = useSelector(selectSearchResults);
  const taskSearchData = useSelector(selectTaskSearchData);
  const taskSearchGridRefreshNonce = useSelector(selectTaskSearchGridRefreshNonce);
  const taskSearchGridRefreshNonceRef = React.useRef(taskSearchGridRefreshNonce);
  const gridState = useSelector(selectGridState);
  const apiRef = useGridApiRef();
  const { userInfo } = useSelector(selectUser);
  const gridPreferences = useSelector((state: RootState) =>
    state.userPreferenceData.userGridPreferences.find(item => item.id === gridTag),
  );
  const userPreferenceLoaded = useSelector(selectUserPreferenceLoaded);
  const [selectedTaskId, setSelectedTaskId] = useState<number>(0);
  const [selectedRootTaskId, setSelectedRootTaskId] = useState<number>(0);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [pendingGridRefreshOnModalClose, setPendingGridRefreshOnModalClose] = useState<boolean>(false);
  const openEditModal = React.useCallback((row: any) => {
    const rowTaskId = Number(row?.taskID);
    const rowParentTaskId = Number(row?.parentTaskID) || rowTaskId;
    if (Number.isFinite(rowTaskId) && rowTaskId > 0) {
      setSelectedTaskId(rowTaskId);
      setSelectedRootTaskId(rowParentTaskId);
      setIsEditModalOpen(true);
    }
  }, []);
  // Set up default filter values
  const absoluteDefaultValues = useMemo(
    () => ({
      [TaskSearchFilterName.assignedTo]: [],
      [TaskSearchFilterName.taskCategory]: [],
      [TaskSearchFilterName.taskName]: [],
      [TaskSearchFilterName.status]: taskDefaultFilterValues[taskSearchFilterName.status],
      [TaskSearchFilterName.dueDate]: null,
      // Sent Date range for Global Search (maps to sentDateMin/sentDateMax in API)
      sentDateRange: null,
      // Follow-Up Yes/No multi-select (backed by isFollowUp boolean in API)
      followUpFilter: [],
      // Follow-Up Date range for Global Search (maps to followUpDateMin/followUpDateMax in API)
      followUpDateRange: null,
      // Priority Yes/No multi-select (backed by priorities array in API)
      priorities: [],
      // Option Approval Yes/No multi-select (backed by isOptionApproval boolean in API)
      optionApprovalFilter: [],
      // Flagged Yes/No multi-select (backed by isFlagged boolean in API)
      flaggedFilter: [],
      // Coverage Yes/No multi-select (backed by isCoverage boolean in API)
      coverageFilter: [],
      [TaskSearchFilterName.candidateAggregate]: initialCandidateAggregateSearch,
      [TaskSearchFilterName.placementId]: [],
      [TaskSearchFilterName.orderId]: [],
      [taskSearchFilterName.facility]: initialFacilityAggregateSearch,
      [TaskSearchFilterName.unit]: initialUnitAggregateSearch,
      [TaskSearchFilterName.createdBy]: [],
      [TaskSearchFilterName.recipients]: [
        {
          id: userInfo?.employeeId,
          value: userInfo?.employeeId,
          name: `${userInfo?.firstName ?? ''} ${userInfo?.lastName ?? ''}`.trim(),
        },
      ],
    }),
    [userInfo?.employeeId, userInfo?.firstName, userInfo?.lastName],
  );

  const formMethods = useForm({ defaultValues: absoluteDefaultValues });
  const { handleSubmit, reset } = formMethods;

  // Sync form from grid state when filters are set (e.g. reload/session restore) so dropdowns match chips
  const filterAttributes = gridState?.filterAttributes;
  const filtersFromState = filterAttributes?.filters;
  const isTaskGridState =
    filterAttributes?.associatedGridName === gridTag &&
    Array.isArray(filtersFromState) &&
    filtersFromState.length > 0;
  const stateValues = useMemo<Record<string, unknown> | null>(
    () =>
      isTaskGridState && Array.isArray(filtersFromState)
        ? Object.fromEntries(filtersFromState as Array<[string, unknown]>)
        : null,
    [isTaskGridState, filtersFromState],
  );
  const activeFlaggedFilter = useMemo(
    () => getSingleBooleanFilterValue(stateValues?.flaggedFilter),
    [stateValues],
  );
  const activeCoverageFilter = useMemo(
    () => getSingleBooleanFilterValue(stateValues?.coverageFilter),
    [stateValues],
  );

  // Reconcile self-name on form values for Recipients / Created By; see
  // `oidc/selfEmployeeNameReconciler` for shared logic. ADO 506080 (Bug 2).
  const reconcileSelfEmployeeName = React.useCallback(
    (entries: unknown) =>
      reconcileSelfEmployeeNameEntries(entries, {
        employeeId: userInfo?.employeeId,
        firstName: userInfo?.firstName,
        lastName: userInfo?.lastName,
      }),
    [userInfo?.employeeId, userInfo?.firstName, userInfo?.lastName],
  );

  useEffect(() => {
    if (isTaskGridState && filtersFromState) {
      const merged: Record<string, unknown> = { ...absoluteDefaultValues, ...stateValues };
      merged[TaskSearchFilterName.recipients] = reconcileSelfEmployeeName(
        merged[TaskSearchFilterName.recipients],
      );
      merged[TaskSearchFilterName.createdBy] = reconcileSelfEmployeeName(
        merged[TaskSearchFilterName.createdBy],
      );
      reset(merged);
    }
  }, [
    absoluteDefaultValues,
    isTaskGridState,
    filtersFromState,
    gridTag,
    reset,
    stateValues,
    reconcileSelfEmployeeName,
  ]);

  const onApplyFilter = (values: any, newSelectedChipFilters?: any) => {
    const selectedChipFilters =
      newSelectedChipFilters ||
      revalidateChipSelection(values, gridState.filterAttributes?.selectedChipFilters || {});

    dispatch(gridStateActions.setPageNumber(1));
    dispatch(
      gridStateActions.setFilterAttributes({
        ...gridState.filterAttributes,
        filterObject: Object.entries(values),
        selectedChipFilters,
        associatedGridName: gridTag,
      }),
    );
    dispatch(gridStateActions.setSelectedFilters(values));
    dispatch(gridStateActions.setSelectedChips(selectedChipFilters));
  };

  const saveAsModalChips = React.useMemo(
    () => (
      <SearchTasksChips
        onApplyFilter={onApplyFilter}
        gridTag={gridTag}
        hideDelete
        setChipExpanded={() => {}}
        chipLabelPrefixes={{
          [TaskSearchFilterName.status]: 'Status',
          [TaskSearchFilterName.candidateAggregate]: 'Candidate',
          [TaskSearchFilterName.unit]: 'Unit',
          [TaskSearchFilterName.createdBy]: 'From',
          [TaskSearchFilterName.recipients]: 'Recipients',
          [taskSearchFilterName.facility]: 'Facility',
          [taskSearchFilterName.oid]: 'OID',
          [taskSearchFilterName.pid]: 'PID',
          sentDateRange: 'Sent Date',
          followUpFilter: 'Follow-Up',
          followUpDateRange: 'Follow-Up Date',
          priorities: 'Priority',
          optionApprovalFilter: 'Option Approval',
          [TaskSearchFilterName.taskCategory]: 'Category',
          flaggedFilter: 'Flagged',
          coverageFilter: 'Coverage',
        }}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onApplyFilter, gridTag],
  );

  // Transform API response into tree structure: root task displays parent task fields, subtasks as direct children
  const rows = useMemo(() => {
    if (!taskSearchData?.items || taskSearchData.items.length === 0) {
      return [];
    }

    const flatRows: any[] = [];
    const processedRootIDs = new Set<string | number>();
    
    // Process each parent task from API response
    taskSearchData.items.forEach((parentTask: IParentTaskSearchResponse, parentIndex: number) => {
      // Support both camelCase (API) and Pascal case (mapped); ensure unique id/path even when missing
      const rootID = parentTask.rootTaskID ?? (parentTask as any).rootTaskId ?? `r-${parentIndex}`;
      const parentTaskId = parentTask.taskID ?? (parentTask as any).taskId ?? 0;
      const rootKey = String(rootID);

      const matchingSubTasks = (parentTask.subTasks || []).filter(
        subTask =>
          matchesBooleanFilter(subTask.isFlagged, activeFlaggedFilter) &&
          matchesBooleanFilter(subTask.isCoverage, activeCoverageFilter),
      );
      const renderRootRow =
        matchesBooleanFilter(parentTask.isFlagged, activeFlaggedFilter) &&
        matchesBooleanFilter(parentTask.isCoverage, activeCoverageFilter);

      // Add root task as top-level node with all parent task fields (only once per rootID)
      if (renderRootRow && !processedRootIDs.has(rootKey)) {
        processedRootIDs.add(rootKey);
        flatRows.push({
          ...parentTask,
          id: `root-${rootKey}`,
          path: [rootKey],
          status: parentTask.status || missingField,
          from: parentTask.senderName || missingField,
          recipients: parentTask.recipients?.map(r => `${r.firstName} ${r.lastName}`).join(', ') || missingField,
          sentDate: parentTask.sentDate || missingField,
          isFollowup: parentTask.isFollowup ? 'Yes' : 'No',
          followupDate: parentTask.followupDate || missingField,
          priority: parentTask.priority || missingField,
          subject: parentTask.subject || missingField,
          message: parentTask.text || missingField,
          isOptionApproval: parentTask.isOptionApproval ? 'Yes' : 'No',
          category: parentTask.category || missingField,
          isFlagged: parentTask.isFlagged == null ? missingField : parentTask.isFlagged ? 'Yes' : 'No',
          isCoverage: parentTask.isCoverage ? 'Yes' : 'No',
          coveredFor: parentTask.coveredEmployee?.employeeName || missingField,
          facilityID: idOrDash(parentTask.facility?.facilityId),
          facility: idOrDash(parentTask.facility?.facilityId) === missingField ? missingField : (parentTask.facility?.facilityName || missingField),
          unitID: idOrDash(parentTask.unit?.unitId),
          unit: idOrDash(parentTask.unit?.unitId) === missingField ? missingField : (parentTask.unit?.unitName || missingField),
          orderID: idOrDash(parentTask.orderID),
          placementID: idOrDash(parentTask.placementID),
          candidateID: idOrDash(parentTask.candidate?.candidateID),
          candidate: idOrDash(parentTask.candidate?.candidateID) === missingField ? missingField : (parentTask.candidate?.candidateName || missingField),
          candidateBrands: parentTask.candidate?.brands ?? [],
          contractAffiliateID: idOrDash(parentTask.contractAffiliateID),
          affiliateContactID: idOrDash(parentTask.affiliateContactID),
          facilityContactID: idOrDash(parentTask.facilityContactID),
          taskID: parentTaskId,
          parentTaskID: parentTaskId,
          rootTaskID: rootID,
          isRootNode: true,
        });
      }

      // When boolean filters are active, suppress non-matching context rows.
      // Matching subtasks under a non-matching parent are elevated to top-level rows.
      if (matchingSubTasks.length > 0) {
        matchingSubTasks.forEach((subTask: ISubTaskSearchResponse, subIndex: number) => {
          const subID = subTask.taskID ?? (subTask as any).taskId ?? `s-${parentIndex}-${subIndex}`;
          const subKey = String(subID);
          flatRows.push({
            ...subTask,
            id: renderRootRow ? `task-${rootKey}-${subKey}` : `task-${subKey}`,
            path: renderRootRow ? [rootKey, subKey] : [subKey],
            status: subTask.status || missingField,
            from: subTask.senderName || missingField,
            recipients: subTask.recipients?.map(r => `${r.firstName} ${r.lastName}`).join(', ') || missingField,
            sentDate: subTask.sentDate || missingField,
            isFollowup: subTask.isFollowup ? 'Yes' : 'No',
            followupDate: subTask.followupDate || missingField,
            priority: subTask.priority || missingField,
            subject: subTask.subject || missingField,
            message: subTask.text || missingField,
            isOptionApproval: subTask.isOptionApproval ? 'Yes' : 'No',
            category: subTask.category || missingField,
            isFlagged: subTask.isFlagged == null ? missingField : subTask.isFlagged ? 'Yes' : 'No',
            isCoverage: subTask.isCoverage == null ? missingField : subTask.isCoverage ? 'Yes' : 'No',
            coveredFor: subTask.coveredEmployee?.employeeName || missingField,
            facilityID: idOrDash(subTask.facility?.facilityId),
            facility: idOrDash(subTask.facility?.facilityId) === missingField ? missingField : (subTask.facility?.facilityName || missingField),
            unitID: idOrDash(subTask.unit?.unitId),
            unit: idOrDash(subTask.unit?.unitId) === missingField ? missingField : (subTask.unit?.unitName || missingField),
            orderID: idOrDash(subTask.orderID),
            placementID: idOrDash(subTask.placementID),
            candidateID: idOrDash(subTask.candidate?.candidateID),
            candidate: idOrDash(subTask.candidate?.candidateID) === missingField ? missingField : (subTask.candidate?.candidateName || missingField),
            candidateBrands: subTask.candidate?.brands ?? [],
            contractAffiliateID: idOrDash(subTask.contractAffiliateID),
            affiliateContactID: idOrDash(subTask.affiliateContactID),
            facilityContactID: idOrDash(subTask.facilityContactID),
            taskID: subTask.taskID ?? (subTask as any).taskId ?? 0,
            parentTaskID: parentTaskId,
            rootTaskID: rootID,
          });
        });
      }
    });

    return flatRows;
  }, [activeCoverageFilter, activeFlaggedFilter, taskSearchData?.items]);

  // Define columns
  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        const statusId = params.row?.statusId;
        if (statusId == null) {
          return <Box>{missingField}</Box>;
        }
        return <StatusBadgeGrid statusId={statusId} />;
      },
    },
    {
      field: 'from',
      headerName: 'From',
      width: 150,
      renderCell: (params) => <OverflowTooltipCell value={params.value ?? missingField} />,
    },
    {
      field: 'recipients',
      headerName: 'Recipients',
      width: 200,
      sortable: false,
      renderCell: (params) => <OverflowTooltipCell value={params.value ?? missingField} />,
    },
    {
      field: 'sentDate',
      headerName: 'Sent Date',
      width: 120,
      valueGetter: (params) => toDateOrNull(params.row?.sentDate),
      valueFormatter: (params) =>
        formatDate(params.value instanceof Date ? params.value.toISOString() : null),
      sortComparator: gridDateComparator,
      renderCell: (params) => <OverflowTooltipCell value={formatDate(params.row?.sentDate)} />,
    },
    {
      field: 'isFollowup',
      headerName: 'Follow-Up',
      width: 100,
      renderCell: (params) => <OverflowTooltipCell value={params.value ?? missingField} />,
    },
    {
      field: 'followupDate',
      headerName: 'Follow-Up Date',
      width: 140,
      valueGetter: (params) => toDateOrNull(params.row?.followupDate),
      valueFormatter: (params) =>
        formatDateOnly(params.value instanceof Date ? params.value.toISOString() : null),
      sortComparator: gridDateComparator,
      renderCell: (params) => <OverflowTooltipCell value={formatDateOnly(params.row?.followupDate)} />,
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 100,
      renderCell: (params) => <OverflowTooltipCell value={params.value ?? missingField} />,
    },
    {
      field: 'message',
      headerName: 'Message',
      width: 250,
      sortable: false,
      renderCell: (params) => {
        const messageText = params.row?.message || missingField;
        const hasTaskId = Number(params.row?.taskID) > 0;
        const isLink = messageText !== missingField && hasTaskId;
        return (
          <OverflowTooltipWrapper
            tooltipText={messageText}
            sx={isLink ? {
              color: theme.palette.primary.main,
              cursor: 'pointer',
              '&:hover': { textDecoration: 'underline' },
            } : undefined}
            onClick={(e) => {
              if (isLink) {
                e.stopPropagation();
                openEditModal(params.row);
              }
            }}
          >
            {messageText}
          </OverflowTooltipWrapper>
        );
      },
    },
    {
      field: 'isOptionApproval',
      headerName: 'Option Approval',
      width: 130,
      renderCell: (params) => <OverflowTooltipCell value={params.value ?? missingField} />,
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 150,
      renderCell: (params) => <OverflowTooltipCell value={params.value ?? missingField} />,
    },
    {
      field: 'isFlagged',
      headerName: 'Flagged',
      width: 100,
      renderCell: (params) => <OverflowTooltipCell value={params.value ?? missingField} />,
    },
    {
      field: 'isCoverage',
      headerName: 'Coverage',
      width: 100,
      renderCell: (params) => <OverflowTooltipCell value={params.value ?? missingField} />,
    },
    {
      field: 'coveredFor',
      headerName: 'Covered For',
      width: 150,
      renderCell: (params) => <OverflowTooltipCell value={params.value ?? missingField} />,
    },
    {
      field: 'facilityID',
      headerName: 'Facility ID',
      width: 120,
      sortComparator: numericIdSortComparator,
      renderCell: (params) => {
        const facilityId = params.row?.facilityID;
        if (!facilityId || facilityId === missingField || facilityId === '0') {
          return <Box>{missingField}</Box>;
        }
        const to = `/facility/${facilityId}`;
        return (
          <RouterLink
            to={to}
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
                e.preventDefault();
                dispatch(globalActions.resetAllRecentHistoryDrawers());
                dispatch(globalSearchActions.setDrawerData({
                  open: true,
                  data: { displayValue: facilityId, type: 2 },
                }));
              }
            }}
          >
            {facilityId}
          </RouterLink>
        );
      },
    },
    {
      field: 'facility',
      headerName: 'Facility',
      width: 200,
      renderCell: (params) => {
        const facilityId = params.row?.facilityID;
        const facilityName = params.row?.facility;
        if (!facilityId || facilityId === missingField || facilityId === '0' || facilityName === missingField) {
          return <Box>{missingField}</Box>;
        }
        const to = `/facility/${facilityId}`;
        return (
          <OverflowTooltipWrapper tooltipText={facilityName}>
              <RouterLink
                to={to}
                style={{
                  color: theme.palette.primary.main,
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: 'inherit',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
                    e.preventDefault();
                    dispatch(globalActions.resetAllRecentHistoryDrawers());
                    dispatch(globalSearchActions.setDrawerData({
                      open: true,
                      data: { displayValue: facilityId, type: 2 },
                    }));
                  }
                }}
              >
                {facilityName}
              </RouterLink>
          </OverflowTooltipWrapper>
        );
      },
    },
    {
      field: 'unitID',
      headerName: 'Unit ID',
      width: 100,
      sortComparator: numericIdSortComparator,
      renderCell: (params) => {
        const unitId = params.row?.unitID;
        const facilityId = params.row?.facilityID;
        const noLink = unitId === missingField || unitId === '0' || facilityId === missingField || facilityId === '0';
        if (noLink) {
          return <Box>{missingField}</Box>;
        }
        const to = `/facility/${facilityId}/unit/${unitId}`;
        return (
          <RouterLink
            to={to}
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {unitId}
          </RouterLink>
        );
      },
    },
    {
      field: 'unit',
      headerName: 'Unit',
      width: 150,
      renderCell: (params) => {
        const unitId = params.row?.unitID;
        const unitName = params.row?.unit;
        const facilityId = params.row?.facilityID;
        const noLink = unitId === missingField || unitId === '0' || unitName === missingField || facilityId === missingField || facilityId === '0';
        if (noLink) {
          return <Box>{missingField}</Box>;
        }
        const to = `/facility/${facilityId}/unit/${unitId}`;
        return (
          <OverflowTooltipWrapper tooltipText={unitName}>
            <RouterLink
              to={to}
              style={{
                color: theme.palette.primary.main,
                textDecoration: 'underline',
                cursor: 'pointer',
                fontSize: 'inherit',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {unitName}
            </RouterLink>
          </OverflowTooltipWrapper>
        );
      },
    },
    {
      field: 'orderID',
      headerName: 'Order ID',
      width: 100,
      sortComparator: numericIdSortComparator,
      renderCell: (params) => {
        const orderId = params.row?.orderID;
        if (!orderId || orderId === missingField || orderId === '0') {
          return <Box>{missingField}</Box>;
        }
        const to = `/order/${orderId}`;
        return (
          <RouterLink
            to={to}
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
                e.preventDefault();
                dispatch(globalActions.resetAllRecentHistoryDrawers());
                dispatch(globalSearchActions.setDrawerData({
                  open: true,
                  data: { displayValue: orderId, type: 1 },
                }));
              }
            }}
          >
            {orderId}
          </RouterLink>
        );
      },
    },
    {
      field: 'placementID',
      headerName: 'Placement ID',
      width: 130,
      sortComparator: numericIdSortComparator,
      renderCell: (params) => {
        const placementId = params.row?.placementID;
        if (!placementId || placementId === missingField || placementId === '0') {
          return <Box>{missingField}</Box>;
        }
        const to = `/placement/${placementId}`;
        return (
          <RouterLink
            to={to}
            style={{
              color: theme.palette.primary.main,
              textDecoration: 'underline',
              cursor: 'pointer',
              fontSize: 'inherit',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {placementId}
          </RouterLink>
        );
      },
    },
    {
      field: 'candidateID',
      headerName: 'Candidate ID',
      width: 130,
      sortComparator: numericIdSortComparator,
      renderCell: (params) => {
        const candidateId = params.row?.candidateID;
        if (!candidateId || candidateId === missingField || candidateId === '0') {
          return <Box>{missingField}</Box>;
        }
        return (
          <TaskSearchCandidateBrandCell
            candidateId={candidateId}
            displayText={candidateId}
            brands={params.row?.candidateBrands}
            fallbackBrandId={userInfo?.companyId}
            dispatch={dispatch}
            theme={theme}
          />
        );
      },
    },
    {
      field: 'candidate',
      headerName: 'Candidate',
      width: 200,
      renderCell: (params) => {
        const candidateId = params.row?.candidateID;
        const candidateName = params.row?.candidate;
        if (!candidateId || candidateId === missingField || candidateId === '0' || candidateName === missingField) {
          return <Box>{missingField}</Box>;
        }
        return (
          <TaskSearchCandidateBrandCell
            candidateId={candidateId}
            displayText={candidateName}
            brands={params.row?.candidateBrands}
            fallbackBrandId={userInfo?.companyId}
            dispatch={dispatch}
            theme={theme}
            showOverflowTooltip
          />
        );
      },
    },
    {
      field: 'contractAffiliateID',
      headerName: 'Contract Affiliate ID',
      width: 130,
      sortComparator: numericIdSortComparator,
      renderCell: (params) => <OverflowTooltipCell value={params.value ?? missingField} />,
    },
    {
      field: 'affiliateContactID',
      headerName: 'Affiliate Contact ID',
      width: 130,
      sortComparator: numericIdSortComparator,
      renderCell: (params) => <OverflowTooltipCell value={params.value ?? missingField} />,
    },
    {
      field: 'facilityContactID',
      headerName: 'Facility Contact ID',
      width: 130,
      sortComparator: numericIdSortComparator,
      renderCell: (params) => <OverflowTooltipCell value={params.value ?? missingField} />,
    },
  ], [dispatch, theme, openEditModal, userInfo?.companyId]);

  const [gridColumns, setGridColumns] = React.useState<GridColDef[]>(columns);

  const mergeColumnsWithOrderAndWidth = React.useCallback(
    (baseColumns: GridColDef[], orderedColumns: GridColDef[]): GridColDef[] => {
      const colsByField = new Map(baseColumns.map(col => [col.field, col]));
      const orderedFields = orderedColumns.map(col => col.field);
      const ordered = orderedFields
        .map(field => {
          const def = colsByField.get(field);
          if (!def) return undefined;
          const prev = orderedColumns.find(col => col.field === field);
          return prev?.width ? { ...def, width: prev.width } : def;
        })
        .filter(Boolean) as GridColDef[];
      const appended = baseColumns.filter(col => !orderedFields.includes(col.field));
      return [...ordered, ...appended];
    },
    [],
  );

  React.useEffect(() => {
    setGridColumns(prevGridColumns => mergeColumnsWithOrderAndWidth(columns, prevGridColumns));
  }, [columns, mergeColumnsWithOrderAndWidth]);

  const defaultTasksVisibility: Record<string, boolean> = useMemo(
    () => TASKS_DEFAULT_COLUMN_VISIBILITY,
    [],
  );

  const getTasksSessionColumnState = React.useCallback(
    () => getSessionValue(SessionKey.tasksTempColumnOrder, StorageType.sessionStorage),
    [],
  );

  const resolveActiveFilterContext = React.useCallback(() => {
    const filterSet = gridPreferences?.value?.filterAttributes?.filterSet || [];
    const activeFilterIdx = filterSet.findIndex(
      item => item.name === gridPreferences?.value?.selectedFilter?.name,
    );
    return {
      filterSet,
      activeFilterIdx,
      hasActiveFilter: activeFilterIdx !== -1,
    };
  }, [gridPreferences?.value]);

  const resolveColumnLayoutSources = React.useCallback(() => {
    const sessionColState = getTasksSessionColumnState();
    const { filterSet, activeFilterIdx, hasActiveFilter } = resolveActiveFilterContext();

    const columnVisibilityModel = hasActiveFilter
      ? filterSet[activeFilterIdx]?.columnVisibilityModel ?? defaultTasksVisibility
      : gridPreferences?.value?.columnVisibilityModel ??
        sessionColState?.columnVisibilityModel ??
        defaultTasksVisibility;

    const savedColumns = hasActiveFilter
      ? filterSet[activeFilterIdx]?.columns
      : gridPreferences?.value?.columns ?? sessionColState?.columns;

    return {
      filterSet,
      activeFilterIdx,
      hasActiveFilter,
      columnVisibilityModel,
      savedColumns,
    };
  }, [defaultTasksVisibility, getTasksSessionColumnState, gridPreferences?.value, resolveActiveFilterContext]);

  const applySavedColumnsToDefs = React.useCallback(
    (cols: GridColDef[], savedCols: Array<{ field: string; width?: number }>): GridColDef[] => {
      const merged = cols.map(col => {
        const savedEntry = savedCols.find(s => s.field === col.field);
        return savedEntry?.width ? { ...col, width: savedEntry.width } : col;
      });
      return merged.sort((a, b) => {
        const idxA = savedCols.findIndex(s => s.field === a.field);
        const idxB = savedCols.findIndex(s => s.field === b.field);
        return (idxA === -1 ? Number.MAX_SAFE_INTEGER : idxA) - (idxB === -1 ? Number.MAX_SAFE_INTEGER : idxB);
      });
    },
    [],
  );

  const getColumnsInGridOrder = React.useCallback((): GridColDef[] => {
    const allCols = apiRef.current?.getAllColumns?.();
    if (!allCols || allCols.length === 0) return gridColumns;
    const orderedFields = allCols.map(c => c.field).filter(f => !f.startsWith('__'));
    const colsByField = new Map(gridColumns.map(c => [c.field, c]));
    const ordered = orderedFields
      .map(field => {
        const def = colsByField.get(field);
        if (!def) return undefined;
        const gridCol = allCols.find(c => c.field === field);
        return { ...def, width: gridCol?.width ?? def.width };
      })
      .filter(Boolean) as GridColDef[];
    const appended = gridColumns.filter(c => !orderedFields.includes(c.field));
    return [...ordered, ...appended];
  }, [apiRef, gridColumns]);

  // Tree data path function
  const getTreeDataPath = React.useCallback((row: any) => row.path, []);

  // Column visibility model
  const columnVisibilityModel = useMemo(
    () => ({
      ...defaultTasksVisibility,
      ...(gridState.columnVisibilityModel || {}),
    }),
    [gridState.columnVisibilityModel, defaultTasksVisibility],
  );

  const [customColumns, setCustomColumns] = React.useState(false);

  const hasCustomisedColumns = React.useCallback(
    (currentModel: Record<string, boolean>) => {
      const allColumns = apiRef.current?.getAllColumns() ?? [];
      for (const col of allColumns) {
        const isVisibleDefault = defaultTasksVisibility[col.field] !== false;
        const isVisibleCurrent = currentModel[col.field] !== false;
        if (isVisibleDefault !== isVisibleCurrent) return true;
      }
      return false;
    },
    [apiRef, defaultTasksVisibility],
  );

  const hasCustomColumnLayout = React.useCallback(() => {
    const defaultFields = columns.map(col => col.field).join(',');
    const currentFields = gridColumns.map(col => col.field).join(',');
    if (defaultFields !== currentFields) return true;

    return gridColumns.some(col => {
      const defaultCol = columns.find(defaultColumn => defaultColumn.field === col.field);
      return defaultCol?.width != null && col.width != null && defaultCol.width !== col.width;
    });
  }, [columns, gridColumns]);

  const updateCustomColumnsState = React.useCallback(
    (visibilityModel: Record<string, boolean>) => {
      setCustomColumns(hasCustomisedColumns(visibilityModel) || hasCustomColumnLayout());
    },
    [hasCustomColumnLayout, hasCustomisedColumns],
  );

  const persistColumnLayout = React.useCallback(
    (
      columnsToSave: Array<{ field: string; width?: number }>,
      visibilityModel: Record<string, boolean>,
    ) => {
      if (!userPreferenceLoaded) return;

      const { filterSet, activeFilterIdx, hasActiveFilter } = resolveActiveFilterContext();

      if (!hasActiveFilter) {
        setSessionValue(
          SessionKey.tasksTempColumnOrder,
          {
            columns: columnsToSave,
            columnVisibilityModel: visibilityModel,
          },
          StorageType.sessionStorage,
        );
      }

      let newFilterSet = filterSet;
      if (hasActiveFilter) {
        newFilterSet = _.cloneDeep(filterSet);
        newFilterSet[activeFilterIdx].columns = columnsToSave;
        newFilterSet[activeFilterIdx].columnVisibilityModel = visibilityModel;
      }

      dispatch(
        userDevicePreferenceActions.saveUserGridPreferenceAction({
          id: gridTag,
          value: {
            ...(gridPreferences?.value || {}),
            filterAttributes: {
              ...gridPreferences?.value?.filterAttributes,
              filterSet: newFilterSet,
            },
            ...(hasActiveFilter
              ? {}
              : {
                  columns: columnsToSave,
                  columnVisibilityModel: visibilityModel,
                }),
          },
        }),
      );
    },
    [dispatch, gridPreferences?.value, gridTag, resolveActiveFilterContext, userPreferenceLoaded],
  );

  const saveColumnPreferences = React.useCallback(
    () => {
      if (!userPreferenceLoaded || gridColumns.length === 0) return;
      const allColumns = apiRef.current?.getAllColumns?.();
      if (!allColumns) return;
      const columnsToSave = allColumns
        .filter(col => !col.field.startsWith('__'))
        .map(col => ({ field: col.field, width: col.width }));
      const visibilityModel = gridState.columnVisibilityModel ?? defaultTasksVisibility;

      updateCustomColumnsState(visibilityModel);
      persistColumnLayout(columnsToSave, visibilityModel);
    },
    [
      apiRef,
      defaultTasksVisibility,
      gridColumns.length,
      gridState.columnVisibilityModel,
      persistColumnLayout,
      updateCustomColumnsState,
      userPreferenceLoaded,
    ],
  );

  const handleResetColumns = React.useCallback(() => {
    const allColumns = apiRef.current?.getAllColumns() ?? [];
    const resetModel: Record<string, boolean> = {};
    allColumns.forEach((col) => {
      resetModel[col.field] = defaultTasksVisibility[col.field] !== false;
    });

    dispatch(gridStateActions.setColumnVisibilityModel(resetModel));
    setGridColumns(columns);
    setCustomColumns(false);
    removeSessionValue(SessionKey.tasksTempColumnOrder, StorageType.sessionStorage);

    const { filterSet, activeFilterIdx, hasActiveFilter } = resolveActiveFilterContext();
    let newFilterSet = filterSet;
    if (hasActiveFilter) {
      newFilterSet = _.cloneDeep(filterSet);
      delete newFilterSet[activeFilterIdx].columns;
      delete newFilterSet[activeFilterIdx].columnVisibilityModel;
    }

    const { columns: _discardedColumns, columnVisibilityModel: _discardedVisibility, ...restPrefs } =
      gridPreferences?.value || {};
    if (userPreferenceLoaded) {
      dispatch(
        userDevicePreferenceActions.saveUserGridPreferenceAction({
          id: gridTag,
          value: {
            ...restPrefs,
            filterAttributes: {
              ...gridPreferences?.value?.filterAttributes,
              filterSet: newFilterSet,
            },
            ...(hasActiveFilter ? {} : { columnVisibilityModel: resetModel }),
          },
        }),
      );
    }
  }, [
    apiRef,
    columns,
    defaultTasksVisibility,
    dispatch,
    gridPreferences?.value,
    gridTag,
    resolveActiveFilterContext,
    userPreferenceLoaded,
  ]);

  // Restore column visibility and column order from saved preferences or active filter set.
  // Runs on initial preference load and when the user selects a different saved filter only.
  const lastAppliedFilterName = React.useRef<string | null | undefined>(undefined);
  const hasCompletedInitialHydration = React.useRef(false);
  React.useEffect(() => {
    if (!userPreferenceLoaded || gridColumns.length === 0) return;

    const selectedFilterName = gridPreferences?.value?.selectedFilter?.name ?? null;
    const isInitialLoad = !hasCompletedInitialHydration.current;
    const isFilterSelectionChange =
      hasCompletedInitialHydration.current &&
      lastAppliedFilterName.current !== selectedFilterName;

    if (!isInitialLoad && !isFilterSelectionChange) {
      return;
    }

    const { columnVisibilityModel, savedColumns } = resolveColumnLayoutSources();
    const mergedModel = { ...defaultTasksVisibility, ...(columnVisibilityModel || {}) };
    dispatch(gridStateActions.setColumnVisibilityModel(mergedModel));

    if (savedColumns?.length) {
      setGridColumns(applySavedColumnsToDefs(columns, savedColumns));
      updateCustomColumnsState(mergedModel);
    } else {
      setGridColumns(columns);
      updateCustomColumnsState(mergedModel);
    }

    lastAppliedFilterName.current = selectedFilterName;
    hasCompletedInitialHydration.current = true;
  }, [
    applySavedColumnsToDefs,
    columns,
    defaultTasksVisibility,
    dispatch,
    gridColumns.length,
    gridPreferences?.value?.selectedFilter?.name,
    resolveColumnLayoutSources,
    updateCustomColumnsState,
    userPreferenceLoaded,
  ]);

  // Reset column order when top-level saved columns are cleared (e.g. reset filters / reset columns).
  const prevTopLevelColumns = React.useRef(gridPreferences?.value?.columns);
  React.useEffect(() => {
    const currentColumns = gridPreferences?.value?.columns;
    const columnsWereCleared =
      prevTopLevelColumns.current != null &&
      currentColumns == null &&
      !gridPreferences?.value?.selectedFilter?.name;

    prevTopLevelColumns.current = currentColumns;

    if (!columnsWereCleared) return;

    setGridColumns(columns);
    updateCustomColumnsState(gridState.columnVisibilityModel ?? defaultTasksVisibility);
  }, [
    columns,
    defaultTasksVisibility,
    gridPreferences?.value?.columns,
    gridPreferences?.value?.selectedFilter?.name,
    gridState.columnVisibilityModel,
    updateCustomColumnsState,
  ]);

  // Initialize grid state for tasks; drop filters authored by another grid (e.g.
  // My Placements Workdesk leaves filters without associatedGridName). ADO 506080.
  React.useEffect(() => {
    dispatch(gridStateActions.setGrid(gridTag));
    if (shouldClearForeignFiltersOnGridEntry(filterAttributes, gridTag)) {
      const selectedChipFilters = revalidateChipSelection(absoluteDefaultValues, {});
      dispatch(
        gridStateActions.setFilterAttributes({
          filterObject: Object.entries(absoluteDefaultValues),
          selectedChipFilters,
          associatedGridName: gridTag,
        }),
      );
      dispatch(taskDetailsActions.setTaskSearchData(null));
      reset(absoluteDefaultValues);
    }
    if (!gridState.pageSize) {
      dispatch(gridStateActions.setPageSize(TASKS_DEFAULT_PAGE_SIZE));
    }
    if (!gridState.pageNumber) {
      dispatch(gridStateActions.setPageNumber(1));
    }
  }, [
    absoluteDefaultValues,
    dispatch,
    filterAttributes,
    gridTag,
    gridState.pageNumber,
    gridState.pageSize,
    reset,
  ]);

  // Handle page change
  const handlePageChange = React.useCallback((newPage: number) => {
    dispatch(gridStateActions.setPageNumber(newPage + 1)); // DataGrid uses 0-based, API uses 1-based
    getSearchResults({
      pageNumber: newPage + 1,
      pageSize: gridState.pageSize || TASKS_DEFAULT_PAGE_SIZE,
      sortModel: gridState.sortedColumn,
    });
  }, [dispatch, getSearchResults, gridState.pageSize, gridState.sortedColumn]);

  // Handle page size change
  const handlePageSizeChange = React.useCallback((newPageSize: number) => {
    dispatch(gridStateActions.setPageSize(newPageSize));
    dispatch(gridStateActions.setPageNumber(1)); // Reset to first page when changing page size
    getSearchResults({
      pageNumber: 1,
      pageSize: newPageSize,
      sortModel: gridState.sortedColumn,
    });
  }, [dispatch, getSearchResults, gridState.sortedColumn]);

  const xGridSortModel = React.useMemo(
    () => toGridSortModel(gridState.sortedColumn),
    [gridState.sortedColumn],
  );

  const onSortModelChange = React.useCallback(
    (model: ReturnType<typeof toGridSortModel>) => {
      const sortedColumn = fromGridSortModel(model);
      const first = model?.[0];

      if (first?.field && !isServerSortableField(first.field)) {
        return;
      }

      dispatch(gridStateActions.setSortedColumn(sortedColumn ?? null));
      dispatch(gridStateActions.setPageNumber(1));
      getSearchResults({
        pageNumber: 1,
        pageSize: gridState.pageSize || TASKS_DEFAULT_PAGE_SIZE,
        sortModel: sortedColumn,
      });
    },
    [dispatch, getSearchResults, gridState.pageSize],
  );

  const handleRefresh = React.useCallback(() => {
    getSearchResults({
      pageNumber: gridState.pageNumber || 1,
      pageSize: gridState.pageSize || TASKS_DEFAULT_PAGE_SIZE,
      sortModel: gridState.sortedColumn,
      multiSortedColumns: gridState.multiSortedColumns,
    });
  }, [getSearchResults, gridState.pageNumber, gridState.pageSize, gridState.sortedColumn, gridState.multiSortedColumns]);

  React.useEffect(() => {
    if (taskSearchGridRefreshNonce === taskSearchGridRefreshNonceRef.current) return;
    taskSearchGridRefreshNonceRef.current = taskSearchGridRefreshNonce;
    handleRefresh();
  }, [taskSearchGridRefreshNonce, handleRefresh]);

  // Get total row count from paging
  const rowCount = taskSearchData?.paging?.total || 0;
  // Get current page and page size from gridState
  const currentPage = gridState.pageNumber ? gridState.pageNumber - 1 : 0; // Convert to 0-based for DataGrid
  const currentPageSize = gridState.pageSize || TASKS_DEFAULT_PAGE_SIZE;

  // Save column widths when a column is resized
  React.useEffect(() => {
    const unsubscribe = apiRef.current?.subscribeEvent?.('columnResizeStop', () => {
      setGridColumns(getColumnsInGridOrder());
      saveColumnPreferences();
    });
    return () => { unsubscribe?.(); };
  }, [apiRef, getColumnsInGridOrder, saveColumnPreferences]);

  // Expand all tree nodes by default when rows change
  React.useEffect(() => {
    if (!rows || rows.length === 0) return;
    const allIds = apiRef.current?.getAllRowIds?.();
    allIds?.forEach((id: any) => {
      apiRef.current?.setRowChildrenExpansion(id, true);
    });
  }, [rows, apiRef]);

  const rootTaskIds = useMemo(
    () =>
      rows
        .filter(row => {
          const originalTaskId = row?.originalTaskId ?? row?.originalTaskID;
          return row?.isRootNode && Number(row?.taskID) > 0 && (originalTaskId == null || originalTaskId === 0);
        })
        .map(row => Number(row.taskID)),
    [rows],
  );

  const currentRootTaskIndex = useMemo(() => {
    const lookupTaskId = selectedRootTaskId || selectedTaskId;
    return rootTaskIds.indexOf(lookupTaskId);
  }, [rootTaskIds, selectedRootTaskId, selectedTaskId]);

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(filter => onApplyFilter(filter))}>
        <Box id="tasks-page-outer" className={classes.root} sx={{ backgroundColor: theme.palette.framework.system.whisper }}>
          <Grid container wrap="nowrap" alignItems="center" justifyContent="space-between" sx={{ marginBottom: '12px' }}>
            <Grid item>
              <Box className="page-title">
                {t('search.globalSearch.tasks.title')}
              </Box>
            </Grid>
            <Grid item sx={{ display: 'flex', textAlign: 'end' }}>
              <SearchFilterAction
                category={SearchType.tasks}
                searchString={searchString ?? ''}
                chips={saveAsModalChips}
                onFilterSetChange={val => onApplyFilter(val)}
              />
            </Grid>
          </Grid>
          <Paper
            id="tasks-page-paper"
            elevation={0}
            sx={{
              backgroundColor: 'white',
              border: '1px solid rgba(224, 224, 224, 1)',
              borderRadius: '4px',
              padding: '12px',
              minHeight: 'calc(100vh - 130px)',
            }}
          >
            <LayoutGrid container direction="row" justifyContent="space-between" classes={{ container: classes.container }}>
              <LayoutGridItem
                container
                direction="column"
                spacing={2}
                classes={{ root: classes.mainPanelRoot, container: classes.container }}
              >
                <LayoutGridItem id="tasks-page-filters" xs={12}>
                  <TaskSearchFilters defaultFilterValues={absoluteDefaultValues} />
                </LayoutGridItem>

                <LayoutGridItem id="tasks-page-chips" xs={12}>
                  <SearchTasksChips
                    onApplyFilter={onApplyFilter}
                    gridTag={gridTag}
                    hideDelete={false}
                    setChipExpanded={() => {}}
                    chipLabelPrefixes={{
                      [TaskSearchFilterName.status]: 'Status',
                      [TaskSearchFilterName.candidateAggregate]: 'Candidate',
                      [TaskSearchFilterName.unit]: 'Unit',
                      [TaskSearchFilterName.createdBy]: 'From',
                      [TaskSearchFilterName.recipients]: 'Recipients',
                      [taskSearchFilterName.facility]: 'Facility',
                      [taskSearchFilterName.oid]: 'OID',
                      [taskSearchFilterName.pid]: 'PID',
                      sentDateRange: 'Sent Date',
                      followUpFilter: 'Follow-Up',
                      followUpDateRange: 'Follow-Up Date',
                      priorities: 'Priority',
                      optionApprovalFilter: 'Option Approval',
                      [TaskSearchFilterName.taskCategory]: 'Category',
                      flaggedFilter: 'Flagged',
                      coverageFilter: 'Coverage',
                    }}
                  />
                </LayoutGridItem>

                <LayoutGridItem id="tasks-page-content" xs={12}>
              <DataGridPro
                apiRef={apiRef}
                treeData
                getTreeDataPath={getTreeDataPath}
                rows={rows}
                columns={gridColumns}
                loading={loading}
                density="compact"
                getRowClassName={(params) => (params.row?.isRootNode ? 'tasks-root-row' : '')}
                disableColumnFilter
                disableColumnMenu
                pagination
                paginationMode="server"
                sortingMode="server"
                sortModel={xGridSortModel}
                onSortModelChange={onSortModelChange}
                page={currentPage}
                pageSize={currentPageSize}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                rowsPerPageOptions={[50, 100, 150, 200, 250]}
                rowCount={rowCount}
                onRowDoubleClick={(params) => openEditModal(params.row)}
                columnVisibilityModel={columnVisibilityModel}
                onColumnOrderChange={() => {
                  const orderedColumns = getColumnsInGridOrder();
                  const currentFields = gridColumns.map(c => c.field).join(',');
                  const newFields = orderedColumns.map(c => c.field).join(',');
                  if (currentFields !== newFields) {
                    setGridColumns(orderedColumns);
                  }
                  saveColumnPreferences();
                }}
                onColumnVisibilityModelChange={(model) => {
                  dispatch(gridStateActions.setColumnVisibilityModel(model));

                  const { hasActiveFilter } = resolveActiveFilterContext();
                  if (!hasActiveFilter) {
                    setSessionValue(
                      SessionKey.tasksTempColumnOrder,
                      {
                        ...getTasksSessionColumnState(),
                        columnVisibilityModel: model,
                      },
                      StorageType.sessionStorage,
                    );
                  }

                  const columnsSnapshot =
                    apiRef.current
                      ?.getAllColumns?.()
                      ?.filter(col => !col.field.startsWith('__'))
                      .map(col => ({ field: col.field, width: col.width })) ??
                    gridColumns.map(col => ({ field: col.field, width: col.width }));

                  persistColumnLayout(columnsSnapshot, model);
                  updateCustomColumnsState(model);
                }}
                groupingColDef={{
                  headerName: 'Subject',
                  width: 300,
                  sortable: false,
                  renderCell: (params) => {
                    const subjectText = params.row?.subject || missingField;
                    const hasTaskId = Number(params.row?.taskID) > 0;
                    const isLink = subjectText !== missingField && hasTaskId;
                    return (
                      <OverflowTooltipWrapper
                        tooltipText={subjectText}
                        sx={{
                          overflow: 'hidden',
                          '& .MuiDataGrid-treeDataGroupingCell > span': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            ...(isLink && {
                              color: theme.palette.primary.main,
                              cursor: 'pointer',
                              '&:hover': { textDecoration: 'underline' },
                            }),
                          },
                        }}
                        onClick={(e) => {
                          if (isLink && !(e.target as HTMLElement).closest('.MuiDataGrid-treeDataGroupingCellToggle')) {
                            e.stopPropagation();
                            openEditModal(params.row);
                          }
                        }}
                      >
                        <GridTreeDataGroupingCell
                          {...params}
                          formattedValue={subjectText}
                        />
                      </OverflowTooltipWrapper>
                    );
                  },
                }}
                components={{
                  Pagination: () => <TasksGridPagination loading={loading} />,
                  Toolbar: XGridToolbar,
                }}
                componentsProps={{
                  toolbar: {
                    title: (
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#333333' }}>
                        {`Tasks (${rowCount})`}
                      </Typography>
                    ),
                    refreshButton: true,
                    refreshGrid: handleRefresh,
                    columnSelector: true,
                    resetButton: customColumns,
                    resetColumns: handleResetColumns,
                    moreOption: true,
                    exportButton: true,
                    filterButton: false,
                    setColumnsButtonEl: () => {},
                    setFilterButtonEl: () => {},
                    panelOpen: '',
                    leftActions: [
                      {
                        KeyId: 'expand-all',
                        Icon: () => null,
                        renderer: () => (
                          <Button
                            variant="text"
                            sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '12px', color: theme.palette.primary.main, minWidth: 0 }}
                          >
                            Expand All
                          </Button>
                        ),
                        onClick: () => {
                          const allIds = apiRef.current?.getAllRowIds();
                          allIds?.forEach((id) => {
                            apiRef.current?.setRowChildrenExpansion(id, true);
                          });
                        },
                      } as IXGridBulkActionButton,
                      {
                        KeyId: 'collapse-all',
                        Icon: () => null,
                        renderer: () => (
                          <Button
                            variant="text"
                            sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '12px', color: theme.palette.primary.main, minWidth: 0 }}
                          >
                            Collapse All
                          </Button>
                        ),
                        onClick: () => {
                          const allIds = apiRef.current?.getAllRowIds();
                          allIds?.forEach((id) => {
                            apiRef.current?.setRowChildrenExpansion(id, false);
                          });
                        },
                      } as IXGridBulkActionButton,
                    ],
                  },
                }}
                sx={{
                  border: '1px solid rgba(224, 224, 224, 1)',
                  fontSize: '14px',
                  height: 'calc(100vh - 220px)',
                  '& .MuiDataGrid-cell': {
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                    fontSize: '14px',
                    fontWeight: 400,
                  },
                  '& .MuiDataGrid-columnHeaders': {
                    borderBottom: '1px solid rgba(224, 224, 224, 1)',
                    backgroundColor: theme.palette.framework.system.whisper,
                    fontSize: '12px',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    fontSize: '12px',
                  },
                  '& .tasks-root-row': {
                    backgroundColor: '#006FB914',
                  },
                  '& .MuiDataGrid-toolbarContainer .MuiDivider-root': {
                    height: '16px',
                    alignSelf: 'center',
                  },
                  '& .MuiChip-root': {
                    borderWidth: '1px !important',
                    width: 'auto !important',
                    maxWidth: 'none !important',
                  },
                  '& .MuiChip-root .MuiChip-label': {
                    paddingLeft: '12px !important',
                    paddingRight: '12px !important',
                  },
                }}
              />
              </LayoutGridItem>
            </LayoutGridItem>
          </LayoutGrid>
          </Paper>

          <DrawerFitted
            onDrawerClose={() => {
              dispatch(globalSearchActions.setDrawerData({ open: false, data: undefined }));
            }}
            width={450}
            top={0}
            backgroundColor={theme.palette.framework.system.whisper}
            open={drawerData?.open ?? false}
            shadow={false}
          >
            {drawerData?.open && drawerData?.data ? (
              <SearchDrawerContent category="tasks" data={drawerData?.data} />
            ) : null}
          </DrawerFitted>
          {isEditModalOpen && selectedTaskId > 0 && (
            <ViewEditTaskModal
              taskID={selectedTaskId}
              open={isEditModalOpen}
              onTaskPersistSuccess={() => {
                setPendingGridRefreshOnModalClose(true);
              }}
              showNavigation={rootTaskIds.length > 1}
              onNavigatePrevious={() => {
                if (currentRootTaskIndex <= 0) return;
                const previousRootTaskId = rootTaskIds[currentRootTaskIndex - 1];
                setSelectedTaskId(previousRootTaskId);
                setSelectedRootTaskId(previousRootTaskId);
              }}
              onNavigateNext={() => {
                if (currentRootTaskIndex < 0 || currentRootTaskIndex >= rootTaskIds.length - 1) return;
                const nextRootTaskId = rootTaskIds[currentRootTaskIndex + 1];
                setSelectedTaskId(nextRootTaskId);
                setSelectedRootTaskId(nextRootTaskId);
              }}
              disableNavigatePrevious={currentRootTaskIndex <= 0}
              disableNavigateNext={currentRootTaskIndex < 0 || currentRootTaskIndex >= rootTaskIds.length - 1}
              handleClose={(options) => {
                if (options?.refreshGrid || pendingGridRefreshOnModalClose) {
                  handleRefresh();
                }
                setPendingGridRefreshOnModalClose(false);
                setIsEditModalOpen(false);
                setSelectedTaskId(0);
                setSelectedRootTaskId(0);
              }}
            />
          )}
        </Box>
      </form>
    </FormProvider>
  );
};
